import { BehaviorSubject } from 'rxjs';
import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UntypedFormGroup } from '@angular/forms';
import { Question, Answer } from '_models/question';
import { UserService } from '_services/user.service';
import { UnivPrefsService } from '_services/univ-prefs.service';
import { QuestionsService } from '_services/questions.service';
import {
  COMPOUND_INDEPENDENT_ID,
  DEPENDENCY_CRITERIA,
  DEPENDENCY_INPUT_IDS,
  DependencyCriterion,
  GATEWAY_YES_NO_IDS,
  INDEPENDENT_HOUSEHOLD_PROFILE_IDS,
  isGatewayCriterionSelected,
} from '_services/dependency-gateway';

/**
 * Auto-written answers that should not alone trigger a pending-session snapshot.
 * Includes ensureDependentDefault / empty commitGatewayForm([]) seeds, and
 * parent.siblingCount forced to 0 when household size hides that question.
 */
const GENERATED_ANSWER_DEFAULTS = new Map<string, string>([
  ['student.dependencyStatus', 'Dependent'],
  [COMPOUND_INDEPENDENT_ID, 'no'],
  ['student.maritalStatusCode', '1'],
  ['parent.siblingCount', '0'],
  ...GATEWAY_YES_NO_IDS.map(questionId => [questionId, 'no'] as [string, string]),
]);

interface InvalidTotals {
  DS: string;
  SI: string;
  SF: string;
  HP: string;
  PI: string;
  PA: string;
}

/** In-memory question state, answers, visibility/dependency logic, and progress. Works with the question set loaded by QuestionsService. */
@Injectable()
export class QuestionService {
  private userService = inject(UserService);
  private univPrefService = inject(UnivPrefsService);
  private cloudQuestions = inject(QuestionsService);

  questions: Question[] = [];
  readonly isLoaded = signal(false);
  readonly isLoaded$ = toObservable(this.isLoaded);

  invalidTotals = new BehaviorSubject<InvalidTotals | null>(null);

  private mapQuestions: Map<string, Question> = new Map<string, Question>();
  private gatewaySelectedCriteria: string[] = [];
  /** Section ids before independent-household reassignment, keyed by question id. */
  private originalHouseholdProfileSections = new Map<string, string>();

  // Gets the questions for a form based on its code (e.g. "SF" for Student Finances)
  public getFilteredQuestions(code: string): Question[] {
    this.updateQuestionStates();
    return this.questions.filter(question => question.section_id === code);
  }

  // Called when:
  //    a form retrieves filtered questions,
  //    a form answer is updated,
  //    when a session loads and sets the answers
  public updateQuestionStates() {

    this.setTriggerStatus(); // Must come before set dependency
    this.setDependency();
    this.syncIndependentHouseholdSections();

    this.showHideAllQuestions();
    this.percentValid();

  }



  private showHideAllQuestions() {
    const userIsDependent = this.userService.isDependent;
    const compound = this.getCompoundChoice();
    const compoundResolved = compound === 'yes' || compound === 'no';

    this.questions.forEach(q => {
      // Dependent users should see questions marked as Dependent
      if (userIsDependent) {
        q.hidden = !q.dependent;
      }
      // Independent users should see questions marked as Independent
      if (!userIsDependent) {
        q.hidden = !q.independent;
      }
      // Hide or show based off of efcMethodologyHidden flag;
      q.hidden = q.hidden || (q.efcMethodologyHidden || false);
      // Hide or show based off of trigger logic;
      q.hidden = q.hidden || (q.triggerHide || false);

      if (q.id === 'student.dependencyStatus' || q.id === COMPOUND_INDEPENDENT_ID
        || q.id === 'student.firstName' || q.id === 'student.completingCalculator') {
        q.hidden = true;
      } else if (DEPENDENCY_INPUT_IDS.includes(q.id)) {
        q.hidden = !this.isDependencyProfileVisible(q.id, q.section_id, compoundResolved, userIsDependent);
      }
    });

  }

  /** Birth year (both paths) and marital status (independent) after gateway resolve. */
  private isDependencyProfileVisible(
    id: string,
    sectionId: string,
    compoundResolved: boolean,
    userIsDependent: boolean,
  ): boolean {
    if (!compoundResolved || !['SF', 'HP'].includes(sectionId)) {
      return false;
    }
    if (id === 'student.birthYear') {
      return true;
    }
    if (id === 'student.maritalStatusCode') {
      return !userIsDependent;
    }
    return false;
  }

  //#region Question Logic
  private setDependency() {
    const compound = this.getCompoundChoice();
    this.userService.isDependent = compound !== 'yes';
    this.setQuestionValue('student.dependencyStatus', this.userService.isDependent ? 'Dependent' : 'Independent');
  }

  /** On the student-only path, collect profile/household questions under Household (HP). */
  private syncIndependentHouseholdSections(): void {
    for (const id of INDEPENDENT_HOUSEHOLD_PROFILE_IDS) {
      const question = this.mapQuestions.get(id);
      if (!question) {
        continue;
      }
      let originalSection = this.originalHouseholdProfileSections.get(id);
      if (originalSection === undefined) {
        originalSection = question.section_id;
        this.originalHouseholdProfileSections.set(id, originalSection);
      }
      question.section_id = this.userService.isDependent ? originalSection : 'HP';
    }
  }

  /** Called once when loading a session saved before compound existed. */
  public bootstrapCompoundFromLegacySession(): void {
    if (this.isDependencyResolved()) {
      return;
    }

    const depStatus = this.getAnswer('student.dependencyStatus');
    if (depStatus === 'Independent') {
      this.setQuestionValue(COMPOUND_INDEPENDENT_ID, 'yes');
    } else if (depStatus === 'Dependent') {
      this.setQuestionValue(COMPOUND_INDEPENDENT_ID, 'no');
    }
  }

  private isOlderThan24(): boolean {
    const birthYear = Number(this.getAnswer('student.birthYear'));
    if (!birthYear) {
      return false;
    }
    return this.getAidYearStart() - birthYear >= 24;
  }

  private setTriggerStatus() {
    // If Student is Married or Remarried then ask for spouse's work income
    const studentMaritalStatus = ['2', '3'].indexOf(this.getAnswer('student.maritalStatusCode')) >= 0;
    this.setTriggerState('student.spouseWorkIncome', !studentMaritalStatus);

    // If Student owns a business, ask more detailed business questions
    const businessOwnerIndicatorStudent = (this.getAnswer('student.businessOwnerIndicator') === 'yes');
    this.setTriggerState('student.largeBusinessOwnerIndicator', !businessOwnerIndicatorStudent);
    this.setTriggerState('student.businessValue', !businessOwnerIndicatorStudent);
    this.setTriggerState('student.businessValueAmount', !businessOwnerIndicatorStudent);
    this.setTriggerState('student.businessDebtAmount', !businessOwnerIndicatorStudent);

    // If Student owns a farm, ask more detailed farm questions
    const farmOwnerIndicatorStudent = (this.getAnswer('student.farmOwnerIndicator') === 'yes');
    this.setTriggerState('student.farmValue', !farmOwnerIndicatorStudent);
    this.setTriggerState('student.farmValueAmount', !farmOwnerIndicatorStudent);
    this.setTriggerState('student.farmDebtAmount', !farmOwnerIndicatorStudent);
    this.setTriggerState('student.liveOnFarmIndicator', !farmOwnerIndicatorStudent);

    // If Student owns a home, ask more detailed questions about home
    const homeOwnerIndicatorStudent = (this.getAnswer('student.homeOwnerIndicator') === 'yes');
    this.setTriggerState('student.homeValueAmount', !homeOwnerIndicatorStudent);
    this.setTriggerState('student.homeDebtAmount', !homeOwnerIndicatorStudent);

    // New in 2526
    if (this.questionExists('student.childSupportTrigger')) {
      const studentChildSupportTrigger = (this.getAnswer('student.childSupportTrigger') === 'yes');
      this.setTriggerState('student.childSupportPaidAmount', !studentChildSupportTrigger);
      this.setTriggerState('student.childSupportReceived', !studentChildSupportTrigger);
    }

    // 1040 line items (business income, etc.) only apply when the school asks
    // which tax form and the answer is 1040. Schools that include those
    // questions without parent.taxFormCode should still show them.
    if (this.questionExists('parent.taxFormCode')) {
      const filed1040 = this.getAnswer('parent.taxFormCode') === 'F1040';
      this.setTriggerState('parent.businessIncome', !filed1040);
      this.setTriggerState('parent.otherIncome', !filed1040);
      this.setTriggerState('parent.other1040Income', !filed1040);
      this.setTriggerState('parent.itemizedDeductionAmount', !filed1040);
    }

    // If Parent owns a home, ask more detailed questions about home
    const homeOwnerIndicator = (this.getAnswer('parent.homeOwnerIndicator') === 'yes');
    this.setTriggerState('parent.homeValueAmount', !homeOwnerIndicator);
    this.setTriggerState('parent.homeDebtAmount', !homeOwnerIndicator);
    this.setTriggerState('parent.homePurchasePrice', !homeOwnerIndicator);
    this.setTriggerState('parent.homePurchaseYear', !homeOwnerIndicator);

    // The following questions have IM and FM questions that trigger the same questions.  Need to check if the question exists before setting triggers
    // TODO: Probably good practice on refactor to always check for trigger question before setting triggers.
    // If Parent owns a business, ask more detailed business questions
    if (this.questionExists('parent.businessOwnerIndicator')) {
      const businessOwnerIndicator = (this.getAnswer('parent.businessOwnerIndicator') === 'yes');
      this.setTriggerState('parent.largeBusinessOwnerIndicator', !businessOwnerIndicator);
      this.setTriggerState('parent.businessValueAmount', !businessOwnerIndicator);
      this.setTriggerState('parent.businessDebtAmount', !businessOwnerIndicator);
    }

    // FM Business
    if (this.questionExists('parent.largeBusinessOwnerIndicatorFM')) {
      const FMbusinessOwnerIndicator = (this.getAnswer('parent.largeBusinessOwnerIndicatorFM') === 'yes');
      this.setTriggerState('parent.businessValueAmount', !FMbusinessOwnerIndicator);
      this.setTriggerState('parent.businessDebtAmount', !FMbusinessOwnerIndicator);
    }

    // If Parent owns a farm, ask more detailed farm questions
    if (this.questionExists('parent.farmOwnerIndicator')) {
      const farmOwnerIndicator = (this.getAnswer('parent.farmOwnerIndicator') === 'yes');
      this.setTriggerState('parent.farmValueAmount', !farmOwnerIndicator);
      this.setTriggerState('parent.farmDebtAmount', !farmOwnerIndicator);
      this.setTriggerState('parent.liveOnFarmIndicator', !farmOwnerIndicator);
    }

    // FM Farm
    if (this.questionExists('parent.occupantFarmOwnerIndicator')) {
      const FMfarmOwnerIndicator = (this.getAnswer('parent.occupantFarmOwnerIndicator') === 'yes');
      this.setTriggerState('parent.farmValueAmount', !FMfarmOwnerIndicator);
      this.setTriggerState('parent.farmDebtAmount', !FMfarmOwnerIndicator);
    }



    // If Parent owns real estate, ask more detailed real estate questions
    const realEstateOwnerIndicator = (this.getAnswer('parent.realEstateOwnerIndicator') === 'yes');
    this.setTriggerState('parent.realEstateValueAmount', !realEstateOwnerIndicator);
    this.setTriggerState('parent.realEstateDebtAmount', !realEstateOwnerIndicator);

    // Show questions based on parents marital status.
    const parentsMarried = ['2'].indexOf(this.getAnswer('parent.maritalStatusCode')) >= 0;
    this.setTriggerState('parent.parent2WorkIncome', !parentsMarried);
    // Added 4/12/2023 for Aid Quiz
    const parentHouseholdCount = this.getAnswer('parent.householdCount') || '0';
    const hideSiblingCount = (parentsMarried && parentHouseholdCount === '3') || (parentHouseholdCount === '2');
    this.setTriggerState('parent.siblingCount', hideSiblingCount);
    if (hideSiblingCount) {
      this.setAnswer('parent.siblingCount', '0');
      this.clearSiblingSlotsFrom(1);
    }

    const nonCustodialParentAmount = ['1', '4', '5'].indexOf(this.getAnswer('parent.maritalStatusCode')) >= 0;
    this.setTriggerState('parent.nonCustodialParentAmount', !nonCustodialParentAmount);

    // New in 2526, this will override the trigger above for child support paid amount
    if (this.questionExists('parent.childSupportTrigger')) {
      const parentChildSupportTrigger = (this.getAnswer('parent.childSupportTrigger') === 'yes');
      this.setTriggerState('parent.childSupportPaidAmount', !parentChildSupportTrigger);
      this.setTriggerState('parent.childSupportReceived', !parentChildSupportTrigger);
    } else {
      const childSupportPaidAmount = ['4', '5'].indexOf(this.getAnswer('parent.maritalStatusCode')) >= 0;
      this.setTriggerState('parent.childSupportPaidAmount', !childSupportPaidAmount);
    }

    // If student has siblings ask about their assets
    const siblingCount = this.getAnswer('parent.siblingCount') || '0';
    this.setTriggerState('parent.otherChildrenAssetAmount', siblingCount === '0');
    // Ask age and college status for each sibling (values edited via sibling-list UI).
    const siblingNumber = Number(siblingCount.replace(/\+/g, ''));
    // Don't show non-numbered templates
    this.setTriggerState('parent.siblingAge', true);
    this.setTriggerState('parent.siblingAttendCollegeCode', true);
    this.setTriggerState('parent.siblingCollegeTypeCode', true);
    // Hide unused Firestore template if still present
    this.setTriggerState('parent.siblingCollegeIndicator', true);
    for (let i = 1; i <= 6; i++) {
      // Detail fields stay in the form for calc/progress, but are not rendered as separate rows.
      this.setTriggerState('parent.siblingAge' + i, i > siblingNumber);
      this.setTriggerState('parent.siblingAttendCollegeCode' + i, i > siblingNumber);
      // Only Full/Half time need college type — "No College" (and empty) must not count as unanswered.
      const attendCollege = ['H', 'F'].includes(this.getAnswer('parent.siblingAttendCollegeCode' + i));
      const hideCollegeType = (i > siblingNumber) || !attendCollege;
      if (hideCollegeType) {
        this.setQuestionValue('parent.siblingCollegeTypeCode' + i, '');
      }
      this.setTriggerState('parent.siblingCollegeTypeCode' + i, hideCollegeType);
    }

    // If parent is paying any private school tuition then ask how many children they are paying for
    const pstValue = this.getAnswer('parent.privateSchoolTuitionAmount') || '0';
    const privateSchoolTuition = pstValue !== '0';
    this.setTriggerState('parent.tuitionPaidChildrenCount', !privateSchoolTuition);

    // If student is under 18 hide Veteran question
    const studentBirthYearString = this.getAnswer('student.birthYear')?.substring(0, 4) || '2020';
    const studentBirthYear = Number(studentBirthYearString);
    const studentAge = studentBirthYear ? (new Date).getFullYear() - studentBirthYear : 0;
    this.setTriggerState('student.veteranIndicator', studentAge < 18);

  }
  // Calculates the percent of valid answers for the progress bar
  public percentValid() {
    const relevant = this.questions.filter(question => !question.hidden);
    const answered = relevant.filter(question => String(question.value ?? '').trim() !== '');
    this.userService.progress = relevant.length
      ? Math.round((answered.length / relevant.length) * 100)
      : 100;
    this.invalidTotals.next({
      DS: this.invalidByCategory('DS'),
      SI: this.invalidByCategory('SI'),
      SF: this.invalidByCategory('SF'),
      HP: this.invalidByCategory('HP'),
      PI: this.invalidByCategory('PI'),
      PA: this.invalidByCategory('PA')
    });
  }

  public invalidByCategory(category: string): string {

    let totalInvalid: number;
    if (!this.userService.isDependent && ['PI', 'PA'].indexOf(category) >= 0) {
      totalInvalid = 0;
    } else {
      const totalQuestions = this.questions.filter(question => (question.hidden === false && [category].indexOf(question.section_id) >= 0));
      totalInvalid = totalQuestions.length - totalQuestions.filter(question => (question.value !== '')).length;
    }
    return (totalInvalid === 0) ? '' : totalInvalid.toString();
  }

  /** Same unanswered set that keeps progress below 100% — used by Results preview. */
  public unansweredVisibleQuestions(): Array<{ id: string; label: string; sectionId: string }> {
    return this.questions
      .filter(question => !question.hidden && String(question.value ?? '').trim() === '')
      .map(question => ({
        id: question.id,
        label: question.label?.trim() || question.id,
        sectionId: question.section_id,
      }));
  }

  //#endregion Question Logic

  //#region Answers
  public answers(): Answer[] {
    const answers: Answer[] = [];
    let value = '';
    for (const q of this.questions) {
      value = q.value;
      if (q.format === 'Number' || q.format === 'NumberAllowNegative') {
        // strip $ and comma from value
        try {
          value = String(q.value).replace(/\$/g, '').replace(/,/g, '');
        } catch {
          console.log('ERROR', q);
        }
      }

      // Determine if answer should be hidden:
      // Hidden if: question is hidden OR (user is independent AND section is parent income/assets)
      const isHidden = q.hidden || (!this.userService.isDependent && ['PI', 'PA'].indexOf(q.section_id) >= 0);

      answers.push({
        'questionId': q.id,
        'value': value,
        'hidden': isHidden && q.id !== 'student.dependencyStatus' // Do not hide dependency status
      });
    }
    return answers;
  }

  //#endregion

  //#region Helper Functions
  public getQuestion(id: string): Question | null {
    // WARNING: Returning blank Question object so that we do not have to check if the question was found each time.
    return this.mapQuestions.get(id) || null;
  }

  // Set the answer for a question and return false if the question does not exist
  public setAnswer(id: string, value: string): boolean {
    const q = this.questions.find(question => question.id === id);
    if (q) {

      // Sibling-list owns attend/type option sets (no/F/H and 1–6); Firestore choices may lag.
      const siblingListCode = /^parent\.sibling(AttendCollegeCode|CollegeTypeCode)\d+$/.test(id);
      if (value && q.format === 'Code' && !siblingListCode) {
        if (!q.choices.map(choice => String(choice.value)).includes(String(value))) {
          console.log('Option', value, 'not found in choices for question:', id, 'with choices:', q.choices);
          return false;
        }
      }

      q.value = value;
      return true;
    }
    return false;
  }

  // All values are stored as strings
  public getAnswer(id: string): string {
    const q = this.questions.find(question => question.id === id);
    if (q) {
      return q.value;
    }
    return '';
  }

  public questionExists(id: string): boolean {
    return this.mapQuestions.has(id);
  }

  /** Clears numbered sibling detail answers from `startIndex` through 6 (calc-compatible empty slots). */
  public clearSiblingSlotsFrom(startIndex: number): void {
    for (let i = startIndex; i <= 6; i++) {
      this.setAnswer(`parent.siblingAge${i}`, '');
      this.setAnswer(`parent.siblingAttendCollegeCode${i}`, '');
      this.setAnswer(`parent.siblingCollegeTypeCode${i}`, '');
    }
  }

  /** Keeps reactive form controls aligned after sibling slots are cleared in question state. */
  public syncSiblingSlotControls(form: UntypedFormGroup): void {
    const count = Number((this.getAnswer('parent.siblingCount') || '0').replace(/\+/g, '')) || 0;
    for (let i = count + 1; i <= 6; i++) {
      for (const prefix of [
        'parent.siblingAge',
        'parent.siblingAttendCollegeCode',
        'parent.siblingCollegeTypeCode',
      ]) {
        const control = form.controls[`${prefix}${i}`];
        if (control && control.value !== '') {
          control.setValue('', { emitEvent: false });
        }
      }
    }
  }

  public isDependencyResolved(): boolean {
    const compound = this.getCompoundChoice();
    return compound === 'yes' || compound === 'no';
  }

  public getAvailableCriteria(): DependencyCriterion[] {
    return DEPENDENCY_CRITERIA.filter(c => this.questionExists(c.questionId));
  }

  public inferGatewaySelection(): string[] {
    return DEPENDENCY_CRITERIA
      .filter(c => isGatewayCriterionSelected(
        c.questionId,
        id => this.getAnswer(id),
        () => this.isOlderThan24(),
        this.gatewaySelectedCriteria,
      ))
      .map(c => c.questionId);
  }

  public commitGatewayForm(selectedQuestionIds: string[]): void {
    this.applyGatewayAnswers(new Set(selectedQuestionIds));
  }

  /** Student-and-parents default so later stages are usable before gateway Continue. */
  public ensureDependentDefault(): void {
    if (this.userService.isAidQuiz || this.isDependencyResolved()) {
      return;
    }
    this.commitGatewayForm([]);
  }

  /**
   * True when answers include something beyond auto-written defaults
   * (see GENERATED_ANSWER_DEFAULTS).
   */
  public hasAnswersWorthSaving(): boolean {
    return this.answers().some(answer => {
      const value = String(answer.value ?? '').trim();
      return value !== '' && GENERATED_ANSWER_DEFAULTS.get(answer.questionId) !== value;
    });
  }

  public getAllowedChoiceValues(questionId: string): string[] | null {
    const compound = this.getCompoundChoice();

    if (questionId === 'student.birthYear') {
      if (compound !== 'no') {
        return null;
      }
      const question = this.getQuestion('student.birthYear');
      if (!question?.choices.length) {
        return null;
      }
      const aidYearStart = this.getAidYearStart();
      return question.choices
        .map(choice => String(choice.value))
        .filter(value => {
          const year = Number(String(value).substring(0, 4));
          return !Number.isNaN(year) && aidYearStart - year < 24;
        });
    }

    if (questionId === 'student.maritalStatusCode') {
      if (compound !== 'yes') {
        return null;
      }
      if (!this.gatewaySelectedCriteria.includes('student.maritalStatusCode')) {
        return null;
      }
      return ['2', '3', '4'];
    }

    return null;
  }

  private getCompoundChoice(): string {
    return this.getAnswer(COMPOUND_INDEPENDENT_ID);
  }

  private applyGatewayAnswers(selected: Set<string>): void {
    this.gatewaySelectedCriteria = [...selected];

    for (const id of GATEWAY_YES_NO_IDS) {
      if (this.questionExists(id)) {
        this.setQuestionValue(id, selected.has(id) ? 'yes' : 'no');
      }
    }

    if (this.questionExists('student.maritalStatusCode')) {
      this.setQuestionValue('student.maritalStatusCode', selected.has('student.maritalStatusCode') ? '2' : '1');
    }

    this.setQuestionValue('student.birthYear', '');
    this.setQuestionValue(COMPOUND_INDEPENDENT_ID, selected.size === 0 ? 'no' : 'yes');

    this.updateQuestionStates();
  }

  private setQuestionValue(id: string, value: string): void {
    const q = this.questions.find(question => question.id === id);
    if (q) {
      q.value = value;
    }
  }

  private getAidYearStart(): number {
    const aidYear = this.univPrefService.preferences()?.aidYear ?? '2022-2023';
    let calcYear = Number(aidYear.substring(0, 4));
    if (Number.isNaN(calcYear)) {
      calcYear = new Date().getFullYear();
    }
    return calcYear;
  }

  private setTriggerState(id: string, state: boolean) {
    const q = this.getQuestion(id);
    if (q) {
      q.triggerHide = state;
    }
  }
  //#endregion

  //#region Initial Load
  public processQuestions() {
    this.questions = this.cloudQuestions.questions.value;
    this.populateMap();

    // Set whether we have custom questions for this university
    this.univPrefService.hasCustomQuestions = this.getFilteredQuestions('SI').length > 0;
    // Set initial dependency to true
    this.userService.isDependent = true;

    this.isLoaded.set(true);
  }

  // prepare the map - call once or when question array change
  private populateMap(): void {
    this.mapQuestions = new Map();
    this.originalHouseholdProfileSections = new Map();
    for (const q of this.questions) {
      this.mapQuestions.set(q.id, q);
    }
  }
  //#endregion
}
